import { useEffect, useRef, useState } from 'react';
import { cmsApiClient, CmsApiError } from '@/services/cmsApiClient';
import { useAnalytics } from '@/hooks/useAnalytics';
import { ERROR_API_CALL } from '@/consts/events';
import type { AlignmentOutcomeCategory } from '@/types/registration';

/**
 * RND-36355 — Alignment result polling.
 *
 * After CreateAccountStep fires POST /api/v1/alignment it holds a `submissionId`
 * but never learned the outcome. This hook polls GET /api/v1/alignment/:submissionId
 * until the submission reaches a terminal state. It mirrors the STRUCTURE of the
 * eligibility poll (RND-33881 EligibilityResultStep) — a fixed poll interval, a
 * "taking longer" nudge, and a hard timeout — but runs its own, deliberately
 * shorter schedule: a fixed 3s interval (no back-off), a 20s nudge, and a 90s hard
 * stop, versus EligibilityResultStep's 5s→10s / 30s nudge / 5-min ceiling + email
 * fallback. The tighter ceiling is intentional: alignment is NON-BLOCKING, so on
 * timeout the user still advances to shipping while the backend keeps polling CMS
 * (its own budget is ~5 min) and owns the durable enrollment/careStartDate state —
 * there is no email/retry fallback here. A 90s stop can therefore settle `timeout`
 * while the backend poll is still legitimately in flight; that is by design.
 *
 * Response handling mirrors the CMS Integration Service alignment route
 * (cms-integration-service src/routes/api/v1/alignment.ts):
 * - 202 → status 'pending' | 'processing' → keep polling.
 * - 200 → status 'complete' (+ a recognized `outcome`) → settle `complete`.
 * - 422 → CmsApiError → CMS reported the submission errored/timed out → settle
 *   `error` (reason 'failed'). This is how the live backend surfaces error/timeout.
 * - 404 → CmsApiError → missing submission record → settle `error` (not-found).
 * - 401/403 → CmsApiError → authToken invalid/expired → settle `error` (reason
 *   'failed'); an auth failure can't self-resolve on retry.
 * - network / 5xx / other → transient → keep polling until the hard timeout,
 *   then settle `timeout`.
 * A 200 body whose status is itself 'error'/'timeout' is also treated as terminal
 * (parity with EligibilityResultStep's isTerminal), even though the live backend
 * uses 422 for that case.
 *
 * Polling is inert until both `submissionId` and `authToken` are provided, so a
 * caller can mount the hook unconditionally (Rules of Hooks) and start it later.
 */

export const POLL_INTERVAL_MS = 3_000;
export const TAKING_LONGER_MS = 20_000;
export const TIMEOUT_MS = 90_000;

export type AlignmentPollPhase = 'polling' | 'taking-longer';

export type AlignmentSettlement =
  | { status: 'complete'; outcome?: AlignmentOutcomeCategory; resultCode?: string }
  // A definitive failure. `reason` separates a missing submission record
  // ('not-found', a 404 — a real integrity signal worth alerting on) from CMS
  // reporting the submission itself errored/timed out ('failed', a 422).
  | { status: 'error'; reason: 'not-found' | 'failed' }
  // Never reached a terminal state within TIMEOUT_MS (kept hitting processing /
  // transient failures).
  | { status: 'timeout' };

export type UseAlignmentPollingArgs = {
  submissionId: string | null;
  authToken: string | null;
  onSettled: (result: AlignmentSettlement) => void;
};

export const useAlignmentPolling = ({
  submissionId,
  authToken,
  onSettled,
}: UseAlignmentPollingArgs): { phase: AlignmentPollPhase } => {
  const { trackError } = useAnalytics();
  const [phase, setPhase] = useState<AlignmentPollPhase>('polling');

  // Keep the latest onSettled/trackError without making them effect dependencies:
  // the poll loop must survive re-renders that only change a callback's identity.
  // trackError is memoized on [webregUserId, utmParams] (useAnalytics); utmParams
  // is a RegistrationContext value, so if it were re-created with a new identity and
  // trackError were a dep, the whole effect would tear down and restart — resetting
  // the 20s/90s clocks and re-issuing requests (React's "unnecessary function
  // dependency" footgun). Hold it in a ref, exactly like onSettled.
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const trackErrorRef = useRef(trackError);
  trackErrorRef.current = trackError;

  useEffect(() => {
    if (!submissionId || !authToken) return undefined;

    let settled = false;
    let inFlight = false;
    let cancelled = false;
    // Emit at most one analytics event for the transient (retryable) failure path
    // so a sustained outage doesn't fire an event on every 3s poll tick.
    let transientTracked = false;
    let pollInterval: ReturnType<typeof setInterval> | null = null;
    let takingLongerTimer: ReturnType<typeof setTimeout> | null = null;
    let hardTimeout: ReturnType<typeof setTimeout> | null = null;

    setPhase('polling');

    const stopTimers = () => {
      if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
      if (takingLongerTimer) { clearTimeout(takingLongerTimer); takingLongerTimer = null; }
      if (hardTimeout) { clearTimeout(hardTimeout); hardTimeout = null; }
    };

    const settle = (result: AlignmentSettlement) => {
      if (settled) return;
      settled = true;
      cancelled = true;
      stopTimers();
      onSettledRef.current(result);
    };

    const pollOnce = async () => {
      // inFlight dedupes overlapping interval ticks; `settled` stops any work
      // after the terminal state was reached.
      if (inFlight || settled) return;
      inFlight = true;
      try {
        const res = await cmsApiClient.getAlignmentResult(submissionId, authToken);
        if (cancelled) return;
        if (res.status === 'complete') {
          settle({ status: 'complete', outcome: res.outcome, resultCode: res.resultCode });
        } else if (res.status === 'error' || res.status === 'timeout') {
          // A 200 body whose status is itself terminal error/timeout means CMS
          // failed the submission. The live backend surfaces that as HTTP 422
          // (handled below), but treat the body-level variant as terminal too —
          // parity with EligibilityResultStep — so the hook never spins the full
          // window on a terminal 200 and masks a real backend failure.
          settle({ status: 'error', reason: 'failed' });
        }
        // else: pending / processing — leave the interval to poll again.
      } catch (err) {
        if (cancelled) return;
        const status = err instanceof CmsApiError ? err.status : undefined;
        // 404 (missing submission), 422 (CMS reported error/timeout), and
        // 401/403 (the authToken is invalid/expired) are definitive — stop.
        // An auth failure can't self-resolve on retry, so treat it like the
        // other terminal codes rather than burning the full 90s window.
        // Everything else (network blip, 5xx) is transient: keep polling
        // until the hard timeout so a momentary failure doesn't strand the user.
        const isTerminal = status === 404 || status === 422 || status === 401 || status === 403;
        // Track each terminal failure (they settle, so once) but the transient
        // path only once, so a sustained outage doesn't emit ~30 identical events
        // across the 90s window.
        if (isTerminal || !transientTracked) {
          if (!isTerminal) transientTracked = true;
          trackErrorRef.current({
            event: ERROR_API_CALL,
            properties: { service: 'cms_alignment_poll', status: status ?? 'unknown' },
          });
        }
        if (status === 404) {
          settle({ status: 'error', reason: 'not-found' });
        } else if (status === 422 || status === 401 || status === 403) {
          settle({ status: 'error', reason: 'failed' });
        }
      } finally {
        inFlight = false;
      }
    };

    pollOnce();
    pollInterval = setInterval(() => { pollOnce(); }, POLL_INTERVAL_MS);
    takingLongerTimer = setTimeout(() => {
      if (!settled) setPhase('taking-longer');
    }, TAKING_LONGER_MS);
    hardTimeout = setTimeout(() => { settle({ status: 'timeout' }); }, TIMEOUT_MS);

    return () => {
      cancelled = true;
      stopTimers();
    };
  }, [submissionId, authToken]);

  return { phase };
};
